import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { X, ShoppingCart, Copy, Trophy, Loader2, Flame, Download, Share2, MessageCircle, Mail } from 'lucide-react';

/* ---------- Liste de courses (agrégation des ingrédients sélectionnés) ---------- */
export const ShoppingListModal = ({ recipes, en, onClose }) => {
  const items = useMemo(() => {
    const map = new Map();
    recipes.forEach((r) => {
      r.ingredients.forEach((ing) => {
        const name = en ? ing.en : ing.fr;
        const key = name.toLowerCase();
        const qtyLabel = [ing.qty, ing.unit].filter(Boolean).join(' ').trim();
        if (!map.has(key)) map.set(key, { name, qtys: [] });
        if (qtyLabel) map.get(key).qtys.push(qtyLabel);
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [recipes, en]);

  const buildText = () => items.map((i) => `- ${i.name}${i.qtys.length ? ' : ' + i.qtys.join(' + ') : ''}`).join('\n');

  const copyText = () => {
    const text = buildText();
    if (!navigator.clipboard) { toast.error(en ? 'Copy failed' : 'Copie impossible'); return; }
    navigator.clipboard.writeText(text).then(
      () => toast.success(en ? 'Shopping list copied' : 'Liste copiée dans le presse-papiers'),
      () => toast.error(en ? 'Copy failed' : 'Copie impossible'),
    );
  };

  const sendByEmail = () => {
    const subject = en ? 'My Body-Up shopping list' : 'Ma liste de courses Body-Up';
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildText())}`;
  };

  const sendBySms = () => {
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    window.location.href = `sms:${isIOS ? '&' : '?'}body=${encodeURIComponent(buildText())}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[230] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="shopping-list-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-primary" /> {en ? 'Shopping list' : 'Liste de courses'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="shopping-list-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">{en ? `From ${recipes.length} selected recipe(s)` : `Depuis ${recipes.length} recette(s) sélectionnée(s)`}</p>
        <ul className="space-y-2 mb-5" data-testid="shopping-list-items">
          {items.map((it, i) => (
            <li key={i} className="flex items-start justify-between gap-3 text-sm text-zinc-200 border-b border-white/5 pb-2">
              <span className="font-semibold">{it.name}</span>
              <span className="text-zinc-500 text-xs text-right">{it.qtys.join(' + ')}</span>
            </li>
          ))}
        </ul>
        <button
          onClick={copyText}
          className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-[1.01] transition-transform flex items-center justify-center gap-2"
          data-testid="shopping-list-copy-btn"
        >
          <Copy className="w-4 h-4" /> {en ? 'Copy list' : 'Copier la liste'}
        </button>
        <div className="flex items-center gap-2 mt-2.5">
          <button onClick={sendByEmail} className="flex-1 py-2.5 rounded-xl font-semibold text-sm text-zinc-200 bg-white/5 border border-white/10 hover:border-primary/40 hover:text-white transition-colors flex items-center justify-center gap-1.5" data-testid="shopping-list-email-btn">
            <Mail className="w-3.5 h-3.5" /> {en ? 'Email' : 'Par email'}
          </button>
          <button onClick={sendBySms} className="flex-1 py-2.5 rounded-xl font-semibold text-sm text-zinc-200 bg-white/5 border border-white/10 hover:border-primary/40 hover:text-white transition-colors flex items-center justify-center gap-1.5" data-testid="shopping-list-sms-btn">
            <MessageCircle className="w-3.5 h-3.5" /> SMS
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ---------- Classement des recettes les plus loggées ce mois-ci ---------- */
export const TopOfMonthModal = ({ api, authFetch, en, onClose, onOpenRecipe, recipesCount }) => {
  const [state, setState] = useState('loading'); // loading | ready | empty | error
  const [ranking, setRanking] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    let active = true;
    api('/api/recipes/top-of-month')
      .then((d) => {
        if (!active) return;
        setRanking(d.ranking || []);
        setState((d.ranking || []).length ? 'ready' : 'empty');
      })
      .catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [api]);

  const shareUrl = `${window.location.origin}/suivi-pro`;
  const recipesLabel = recipesCount ? `${recipesCount}` : '180+';
  const shareText = useMemo(() => {
    if (!ranking.length) return '';
    const top3 = ranking.slice(0, 3).map((e, i) => `${i + 1}. ${en ? e.recipe.name_en : e.recipe.name_fr}`).join(' · ');
    return en
      ? `🏆 My top recipes this month on Body-Up: ${top3}. Join Suivi Pro to unlock ${recipesLabel} high-protein recipes: ${shareUrl}`
      : `🏆 Mon top recettes du mois sur Body-Up : ${top3}. Rejoins Suivi Pro pour débloquer ${recipesLabel} recettes protéinées : ${shareUrl}`;
  }, [ranking, en, shareUrl, recipesLabel]);
  const shareLink = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const markShared = () => { api(`/api/recipes/${ranking[0].recipe.id}/share`, { method: 'POST' }).catch(() => {}); };
  const copyShareText = () => {
    if (!navigator.clipboard) { toast.error(en ? 'Copy failed' : 'Copie impossible'); return; }
    navigator.clipboard.writeText(shareText).then(
      () => { toast.success(en ? 'Text copied' : 'Texte copié dans le presse-papiers'); markShared(); },
      () => toast.error(en ? 'Copy failed' : 'Copie impossible'),
    );
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await authFetch(`/api/recipes/top-of-month/pdf${en ? '?lang=en' : ''}`);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'top-recettes-du-mois.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error(en ? 'Could not download the PDF' : 'Impossible de télécharger le PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[230] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="top-of-month-modal"
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-zinc-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2"><Trophy className="w-5 h-5 text-yellow-400" /> {en ? 'My top recipes this month' : 'Mon top recettes du mois'}</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-400" data-testid="top-of-month-close"><X className="w-5 h-5" /></button>
        </div>

        {state === 'loading' && <div className="flex justify-center py-10" data-testid="top-of-month-loading"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>}
        {state === 'error' && <p className="text-red-400 text-sm text-center py-8" data-testid="top-of-month-error">{en ? 'Could not load ranking.' : 'Impossible de charger le classement.'}</p>}
        {state === 'empty' && <p className="text-zinc-500 text-sm text-center py-8" data-testid="top-of-month-empty">{en ? 'No recipe logged this month yet.' : "Aucune recette n'a encore été loggée ce mois-ci."}</p>}
        {state === 'ready' && (
          <>
            <ul className="space-y-2" data-testid="top-of-month-list">
              {ranking.map((entry, i) => (
                <li key={entry.recipe.id}>
                  <button
                    onClick={() => onOpenRecipe(entry.recipe)}
                    className="w-full flex items-center gap-3 rounded-xl bg-zinc-800/60 border border-white/10 hover:border-yellow-500/40 p-2.5 transition-colors text-left"
                    data-testid={`top-of-month-row-${entry.recipe.id}`}
                  >
                    <span className="shrink-0 w-6 text-center font-black text-yellow-400">{i + 1}</span>
                    <img src={entry.recipe.image_url} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-white truncate">{en ? entry.recipe.name_en : entry.recipe.name_fr}</p>
                      <p className="text-xs text-zinc-500 flex items-center gap-1"><Flame className="w-3 h-3 text-primary" /> {Math.round(entry.recipe.macros.calories)} kcal</p>
                    </div>
                    <span className="shrink-0 text-xs font-bold text-yellow-300">{entry.month_count}x</span>
                  </button>
                </li>
              ))}
            </ul>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <a
                href={shareLink} target="_blank" rel="noopener noreferrer" onClick={markShared}
                className="py-3 rounded-xl font-bold text-white bg-[#25D366] hover:brightness-95 transition flex items-center justify-center gap-2"
                data-testid="top-of-month-share-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <button onClick={copyShareText} className="py-3 rounded-xl font-bold text-white bg-zinc-800 border border-white/10 hover:border-primary/40 transition flex items-center justify-center gap-2" data-testid="top-of-month-share-copy">
                <Share2 className="w-4 h-4" /> {en ? 'Copy text' : 'Copier le texte'}
              </button>
            </div>
            <button
              onClick={downloadPdf}
              disabled={downloadingPdf}
              className="w-full mt-2 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-yellow-500 to-primary hover:scale-[1.01] transition-transform flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="top-of-month-pdf-btn"
            >
              {downloadingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {en ? 'Export PDF' : 'Exporter en PDF'}
            </button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};
